import { useState, useEffect, useRef } from "react"
import { Currency } from "helpers/money"
import type DineroFactory from "dinero.js"
import type { Dinero } from "dinero.js"

interface CurrencyItem {
  name: string
  value: number
}

interface ConversionResult {
  totalAmount: Dinero | null
  isConverting: boolean
  errors: string[]
}

export function useCurrencyConversion(
  data: CurrencyItem[],
  targetCurrency: string,
  ratesMap: Record<string, Record<string, number>>,
  options: {
    skipMissingRates?: boolean
    onError?: (error: string) => void
  } = {},
): ConversionResult {
  const [totalAmount, setTotalAmount] = useState<Dinero | null>(null)
  const [isConverting, setIsConverting] = useState<boolean>(false)
  const [errors, setErrors] = useState<string[]>([])

  const dataRef = useRef(data)
  const targetCurrencyRef = useRef(targetCurrency)
  const ratesMapRef = useRef(ratesMap)
  const optionsRef = useRef(options)

  useEffect(() => {
    dataRef.current = data
    targetCurrencyRef.current = targetCurrency
    ratesMapRef.current = ratesMap
    optionsRef.current = options
  }, [data, targetCurrency, ratesMap, options])

  const isMounted = useRef(true)
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!data.length) {
      setTotalAmount(Currency(targetCurrency as DineroFactory.Currency)(0))
      setErrors([])
      return
    }

    const needsConversion = data.some(item => item.name !== targetCurrency)
    if (needsConversion && Object.keys(ratesMap).length === 0) {
      return
    }

    const timeoutId = setTimeout(async () => {
      if (!isMounted.current) return

      setIsConverting(true)
      setErrors([])

      try {
        let total = Currency(targetCurrency as DineroFactory.Currency)(0) as Dinero
        const newErrors: string[] = []

        for (const item of data) {
          try {
            if (!item.name) {
              console.error(`Item missing currency code`, item.name)
            }

            const Amount = Currency(item.name as DineroFactory.Currency)
            const money = Amount(item.value) as Dinero

            if (item.name === targetCurrency) {
              total = total.add(money) as Dinero
              continue
            }

            if (!ratesMap[item.name]) {
              const errorMsg = `Missing rates for ${item.name}`
              newErrors.push(errorMsg)

              if (options.onError) {
                options.onError(errorMsg)
              }

              if (options.skipMissingRates !== false) {
                continue
              }
            }

            const rates = {
              rates: ratesMap[item.name] || {},
            }

            const convertedMoney = (await money.convert(targetCurrency, {
              endpoint: Promise.resolve(rates),
            })) as Dinero

            total = total.add(convertedMoney) as Dinero
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            const errorMsg = `Error converting ${item.name}: ${errorMessage}`
            newErrors.push(errorMsg)

            if (options.onError) {
              options.onError(errorMsg)
            }
          }
        }

        if (isMounted.current) {
          setTotalAmount(total)
          setErrors(newErrors)
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        const errorMsg = `General conversion error: ${errorMessage}`

        if (isMounted.current) {
          setErrors([errorMsg])
          setTotalAmount(Currency(targetCurrency as DineroFactory.Currency)(0))
        }

        if (options.onError) {
          options.onError(errorMsg)
        }
      } finally {
        if (isMounted.current) {
          setIsConverting(false)
        }
      }
    }, 50)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [
    JSON.stringify(data),
    targetCurrency,
    JSON.stringify(ratesMap),
    options.skipMissingRates,
  ])

  return { totalAmount, isConverting, errors }
}
